import React, { useState, useRef } from 'react';
import { useNode } from '@craftjs/core';
import { css, cx, injectGlobal } from '@emotion/css';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import type { NodeCustomProps } from './../../types/craft';
import { useRenderContext } from '../../context/RenderContext';
import type { ButtonVariantsFunc } from '../../styles/buttonVariants';
import { getMediaQueryHeader } from '../../utils/styleHelpers';
import { BUTTON_NODE_NAME, DEFAULT_INTERACTION_EVENT } from './../../utils/constants';
import {
  DEFAULT_VISIBILITY,
  getBoxShadow,
  getBorderRadiusStyle,
  getBorderStyles,
  getGlobalStyleColor,
  getGlobalTextColorStyle,
  getMarginStyle,
  getPaddingStyle,
  getPositionPropsV2,
  getPositionStyle,
  getTypographyStyle,
  getVisibilityClasses,
} from './../../utils/styleHelpers';
import {
  getHrefAttr,
  getTargetAttr,
  getRelHrefAttr,
  getPopupTargetAttr,
} from '../../utils/attributesHelpers';
import type { StylesConfig } from '../../types/styles';
import { ButtonPropsDto } from '@builder4/shared/dto/craft/props/ButtonPropsDto';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getHoverStyles,
} from '../../utils/animationHelpers';

export type ButtonProps = ButtonPropsDto;

export const defaultProps: ButtonProps = {
  fillWidth: false,
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  visibility: { ...DEFAULT_VISIBILITY },
  buttonSize: 'medium',
  buttonVariant: 'primary',
  padding: [6, 16, 6, 16],
  margin: [0, 0, 0, 0],
  borderRadius: [0, 0, 0, 0],
  position: 'relative',
  top: '0px',
  left: '0px',
  right: '',
  bottom: '',
  borderWidth: 0,
  borderColor: undefined,
  textAlign: 'center',
  text: 'Button',
  letterSpacing: 0,

  fontFamily: 'Inter',
  fontWeight: '400',
  fontSize: '16px',
  fontStyle: 'normal',
  color: { r: 255, g: 255, b: 255, a: 1 },
  backgroundColor: { r: 0, g: 0, b: 0, a: 1 },
  appliedGlobalStyle: true,
  buttonType: 'square',
  minWidth: '64px',
  minHeight: '32px',
  width: 'auto',
  flexBasis: 'auto',
  fillSpace: 'no',
  height: 'auto',
  zIndex: 0,

  shadowType: 'none',
  shadowSettings: { x: 0, y: 0, blur: 0, spread: 0, color: '#000000' },
  clickEvent: { ...DEFAULT_INTERACTION_EVENT },
  tablet: {},
  mobile: {},
};

const Button = ({ ...props }: Partial<ButtonProps>) => {
  const { isEditable, environment, fontManager, buttonVariants, styles } = useRenderContext();
  const [textEditingMode, setTextEditingMode] = useState(false);
  const editableSpanRef = useRef<HTMLSpanElement>(null);

  props = {
    ...defaultProps,
    ...props,
  };

  const {
    visibility = DEFAULT_VISIBILITY,
    text,
    fontFamily,
    fontWeight,
    fontStyle,
    clickEvent = { ...DEFAULT_INTERACTION_EVENT },
  } = props;

  const {
    id,
    isLocked,
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode((node) => ({
    id: node.id,
    isLocked: node.data.custom.isLocked,
  }));

  // Function to set cursor position at the end
  const setCursorToEnd = (element: HTMLElement) => {
    const range = globalThis.document.createRange();
    const selection = globalThis.window.getSelection();
    range.selectNodeContents(element);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const enterEditingMode = () => {
    if (isEditable && !isLocked && !textEditingMode) {
      setTextEditingMode(true);
      if (editableSpanRef.current) {
        const element = editableSpanRef.current;
        setTimeout(() => {
          element.focus();
          setCursorToEnd(element);
        }, 0);
      }
    }
  };

  const updateTextProp = (value: string) => {
    setProp((props: ButtonProps) => {
      props.text = value;
    });
    setTextEditingMode(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // prevent newline
      const target = e.target as HTMLSpanElement;
      const text = target.textContent || target.innerHTML || '';
      updateTextProp(text);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    const target = e.target as HTMLSpanElement;
    const text = target.textContent || target.innerHTML || '';
    updateTextProp(text);
  };

  const visibilityStyleClasses = getVisibilityClasses(visibility);

  if (!props.appliedGlobalStyle) {
    const fontFaceRule = fontManager?.getFontFaceCss(fontFamily, fontWeight, fontStyle);
    if (fontFaceRule) injectGlobal(fontFaceRule);
  }

  const href = getHrefAttr(clickEvent);
  const target = getTargetAttr(clickEvent);
  const rel = getRelHrefAttr(clickEvent);
  const isServerRender = environment === 'server';
  const isBuilderMode = environment === 'builder';
  const animationClass = getAnimationInitialClass(props.animationType, environment);
  const animationAttrs = getAnimationDataAttributes(props);
  const popupTarget = getPopupTargetAttr(clickEvent);
  const disablePointerEvents = isEditable && isLocked;
  return (
    <div
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
        }
      }}
      className={cx('button-node', animationClass, buttonNodeStyle(props, isServerRender))}
      {...animationAttrs}
      {...(isBuilderMode ? { 'data-animation-preview': id } : {})}
    >
      <a
        className={cx(
          visibilityStyleClasses,
          isEditable && 'boundary-indicator',
          buttonElementStyle(
            props,
            textEditingMode,
            disablePointerEvents,
            isServerRender,
            styles,
            buttonVariants,
          ),
        )}
        href={href}
        target={target}
        rel={rel}
        data-popup-target={popupTarget}
        onClick={(e) => {
          if (isEditable) {
            e.preventDefault();
          }
        }}
        onDoubleClick={enterEditingMode}
      >
        <span
          ref={editableSpanRef}
          contentEditable={textEditingMode}
          suppressContentEditableWarning
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={editableSpanStyle(props, styles)}
        >
          {text}
        </span>
      </a>
    </div>
  );
};

Button.craft = {
  displayName: BUTTON_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: BUTTON_NODE_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

export default Button;

function buttonNodeStyle(props: Partial<ButtonProps>, isServerRendering: boolean) {
  const currentBpStyles = computeButtonNodeStyles(props as Partial<ButtonProps>);
  const finalStyles: CSSInterpolation = {
    ...currentBpStyles,
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const positionProps = getPositionPropsV2(mobile as Partial<ButtonProps>, props);
    const mobilePositionStyle = computeButtonNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<ButtonProps>);
    finalStyles[getMediaQueryHeader(isServerRendering, 'mobile')] = mobilePositionStyle;
  }
  if (tablet) {
    const positionProps = getPositionPropsV2(tablet as Partial<ButtonProps>, props);
    const tabletPositionStyle = computeButtonNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<ButtonProps>);
    finalStyles[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletPositionStyle;
  }
  return css(finalStyles);
}

function buttonElementStyle(
  props: Partial<ButtonProps>,
  textEditingMode: boolean,
  disablePointerEvents: boolean,
  isServerRendering: boolean,
  activeGlobalStyles: StylesConfig,
  buttonVariants?: ButtonVariantsFunc,
) {
  if (props.appliedGlobalStyle && buttonVariants) {
    const globalClass = buttonVariants({
      variant: props.buttonVariant || 'primary',
      size: props.buttonSize || 'medium',
    });
    const hoverEffectStyles = getButtonHoverEffectStyles(props);

    return cx(
      globalClass,
      css({
        pointerEvents: disablePointerEvents ? 'none' : 'auto',
        cursor: textEditingMode ? 'text' : 'pointer',
        width: '100%',
        textAlign: props.textAlign,
        boxShadow: getBoxShadow(props.shadowType, props.shadowSettings),
        ...hoverEffectStyles,
      }),
    );
  }

  // Fallback to Emotion styles (when detached OR when CVA unavailable)
  const currentBpStyles = computeButtonElementStyles(
    {
      ...props,
      width: '100%',
      height: props.height || 'auto',
    },
    activeGlobalStyles,
  );

  const finalStyles: CSSInterpolation = {
    ...(currentBpStyles as CSSObject),
    display: 'inline-block',
    textDecoration: 'none',
    pointerEvents: disablePointerEvents ? 'none' : 'auto',
    cursor: textEditingMode ? 'text' : 'pointer',
  };
  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeButtonElementStyles(
      mobile as Partial<ButtonProps>,
      activeGlobalStyles,
    );
    finalStyles[getMediaQueryHeader(isServerRendering, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    const tabletStyles = computeButtonElementStyles(
      tablet as Partial<ButtonProps>,
      activeGlobalStyles,
    );
    finalStyles[getMediaQueryHeader(isServerRendering, 'tablet')] = tabletStyles as CSSObject;
  }

  return css(finalStyles);
}

function computeButtonNodeStyles(props: Partial<ButtonProps>) {
  const { position, top, left, right, bottom, positionAnchorsState, zIndex } = props;
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const { fillWidth, fillSpace, flexBasis, width } = props;
  return {
    ...positionStyle,
    zIndex: zIndex || undefined,
    width: width,
    flex: fillWidth ? (fillSpace === 'yes' ? `1 1 ${flexBasis}` : 'unset') : undefined,
  };
}

/** Returns only animation-related hover styles (lift, glow, scale, brighten) and their transition. */
function getButtonHoverEffectStyles(props: Partial<ButtonProps>): CSSObject {
  const hoverStylesObj = getHoverStyles(
    props.hoverEffect,
    props.hoverTransitionDuration,
    props.hoverIntensity,
    !!getBoxShadow(props.shadowType, props.shadowSettings),
  );
  const hoverEffectHover = (hoverStylesObj as Record<string, unknown>)['&:hover'] as
    | CSSObject
    | undefined;

  const result: CSSObject = {
    ...(hoverStylesObj.transition ? { transition: hoverStylesObj.transition } : {}),
    ...(hoverEffectHover &&
      Object.keys(hoverEffectHover).length > 0 && {
        '&:hover': hoverEffectHover,
      }),
  };
  return result;
}

function computeButtonElementStyles(props: Partial<ButtonProps>, activeGlobalStyles: StylesConfig) {
  const {
    borderRadius,
    padding,
    margin,
    borderWidth,
    borderColor,
    hoverBorderColor,
    backgroundColor,
    hoverBackgroundColor,
    letterSpacing,
    fontSize,
    textAlign,
    fontFamily,
    fontWeight,
    fontStyle,
    width,
    height,
    appliedGlobalStyle,
    shadowType,
    shadowSettings,
  } = props;

  const paddingStyle = padding ? getPaddingStyle(padding) : {};
  const marginStyle = margin ? getMarginStyle(margin) : {};
  const borderStyles = borderWidth
    ? getBorderStyles(borderWidth, borderColor, activeGlobalStyles.colors)
    : {};
  const borderRadiusStyle = borderRadius ? getBorderRadiusStyle(borderRadius) : {};
  const textAlignStyle = textAlign ? getTypographyStyle({ textAlign }) : {};
  const typographyStyle = {
    ...textAlignStyle,
    fontFamily: fontFamily || undefined,
    fontWeight: fontWeight || undefined,
    fontStyle: fontStyle || undefined,
    fontSize: fontSize || undefined,
    letterSpacing: letterSpacing ? `${letterSpacing}em` : undefined,
  };

  const background = getGlobalStyleColor(backgroundColor, activeGlobalStyles.colors);
  const hoverBg = hoverBackgroundColor
    ? getGlobalStyleColor(hoverBackgroundColor, activeGlobalStyles.colors)
    : undefined;
  const hoverBorder = hoverBorderColor
    ? getGlobalStyleColor(hoverBorderColor, activeGlobalStyles.colors)
    : undefined;

  const hoverEffectStyles = getButtonHoverEffectStyles(props);
  const hoverEffectHover = (hoverEffectStyles as Record<string, unknown>)['&:hover'] as
    | CSSObject
    | undefined;
  const hoverBlock: CSSObject = {
    ...(hoverBg && { background: hoverBg }),
    ...(hoverBorder && { borderColor: hoverBorder }),
    ...hoverEffectHover,
  };

  const style = {
    ...typographyStyle,
    ...borderRadiusStyle,
    ...paddingStyle,
    ...marginStyle,
    ...borderStyles,
    background,
    width,
    height,
    ...(hoverEffectStyles.transition ? { transition: hoverEffectStyles.transition } : {}),
    boxShadow: getBoxShadow(shadowType, shadowSettings),
    ...(Object.keys(hoverBlock).length > 0 && { '&:hover': hoverBlock }),
    '&:hover span': {
      ...(props.hoverTextColor &&
        !appliedGlobalStyle && {
          ...getGlobalTextColorStyle(props.hoverTextColor, activeGlobalStyles.colors),
        }),
    },
  };

  return style;
}

function editableSpanStyle(props: Partial<ButtonProps>, activeGlobalStyles: StylesConfig) {
  const { color, appliedGlobalStyle } = props;
  const colorStyle =
    !appliedGlobalStyle && getGlobalTextColorStyle(color, activeGlobalStyles.colors);
  return css({
    ...colorStyle,
    outline: 'none',
  });
}
